<div class="layout-topbar">
  <div class="layout-topbar__start">
    @if (layoutService.isMobileLayout()) {
      <button
        type="button"
        class="layout-topbar__menu-toggle"
        i18n-aria-label
        aria-label="Open navigation menu"
        [attr.aria-expanded]="layoutService.isMobileNavOpen()"
        (click)="toggleMobileNav()">
        <i class="fa-regular fa-bars" aria-hidden="true"></i>
      </button>
    }

    <img class="layout-topbar__logo" [src]="brandLogoUrl()" [alt]="brandLogoAltText() ?? 'Pulse Logo'" />
    <img class="layout-topbar__wordmark" [src]="brandWordmarkUrl()" [alt]="brandWordmarkAltText() ?? 'Lumatrak Pulse'" />
  </div>

  <div class="layout-topbar__actions">
    <pulse-topbar-menu
      [open]="accountPanelOpen()"
      (openChange)="onAccountMenuOpenChange($event)"
      [panelClass]="accountPanelClass()"
      i18n-ariaLabel
      ariaLabel="Account menu">
      <span pulseTopbarMenuTrigger class="layout-topbar__user-trigger">
        <span class="layout-topbar__user-avatar">{{ userInitials() }}</span>
        <span class="layout-topbar__user-identity">
          <span class="layout-topbar__user-name">{{ userAccountSummary().displayName }}</span>
          <span class="layout-topbar__user-company">{{ userAccountSummary().companyName }}</span>
        </span>
      </span>

      @if (canSwitchAccount()) {
        <div class="account-menu__profile">
          <span class="account-menu__avatar">{{ userInitials() }}</span>
          <div class="account-menu__profile-details">
            <div class="account-menu__name">{{ userAccountSummary().displayName }}</div>
            <div class="account-menu__email">{{ userAccountSummary().email }}</div>
          </div>
        </div>

        <div class="account-menu__divider"></div>

        <div class="account-menu__switch-caption" i18n>Switch company</div>

        <div class="account-menu__switch-list">
          <ng-scrollbar class="account-menu__scroll" visibility="hover">
            @if (hasTenantGroups()) {
              @for (tenant of accountTenantGroups(); track tenant.tenantId) {
                @let isExpanded = isTenantExpanded(tenant.tenantId);

                <section class="account-menu__tenant" [class.account-menu__tenant--expanded]="isExpanded">
                  <button
                    type="button"
                    class="account-menu__tenant-toggle"
                    [disabled]="isSwitchingAccount()"
                    [attr.aria-expanded]="isExpanded"
                    (click)="toggleTenant(tenant.tenantId)">
                    <span class="account-menu__org-tile" aria-hidden="true">
                      <i class="fa-regular fa-building"></i>
                    </span>
                    <span class="account-menu__tenant-name">{{ tenant.tenantName }}</span>
                    <span class="account-menu__tenant-count">{{ tenant.companies.length }}</span>
                    @if (!isExpanded && tenantContainsCurrentCompany(tenant)) {
                      <span class="account-menu__tenant-current-dot" aria-hidden="true"></span>
                    }
                    <i
                      class="account-menu__tenant-caret fa-solid"
                      [class.fa-chevron-up]="isExpanded"
                      [class.fa-chevron-down]="!isExpanded"
                      aria-hidden="true"></i>
                  </button>

                  @if (isExpanded) {
                    <div class="account-menu__companies">
                      @for (company of tenant.companies; track company.entityRelationshipId) {
                        @let isCurrent = isCompanyCurrent(company.entityRelationshipId);

                        <button
                          type="button"
                          class="account-menu__company"
                          [class.account-menu__company--current]="isCurrent"
                          [disabled]="isSwitchingAccount()"
                          (click)="selectCompany(company.entityRelationshipId)">
                          <span class="account-menu__company-name">{{ company.companyName }}</span>
                          @if (isCurrent) {
                            <span class="account-menu__company-check" aria-hidden="true">
                              <i class="fa-solid fa-check"></i>
                            </span>
                          } @else {
                            <span class="account-menu__company-radio" aria-hidden="true"></span>
                          }
                        </button>
                      }
                    </div>
                  }
                </section>
              }
            } @else {
              <p class="account-menu__empty" i18n>No account relationships found for your user.</p>
            }
          </ng-scrollbar>
        </div>

        <div class="account-menu__divider"></div>
      } @else {
        <div class="account-menu__profile">
          <span class="account-menu__avatar">{{ userInitials() }}</span>
          <div class="account-menu__profile-details">
            <div class="account-menu__name">{{ userAccountSummary().displayName }}</div>
            <div class="account-menu__email">{{ userAccountSummary().email }}</div>
          </div>
        </div>

        <div class="account-menu__divider"></div>
      }

      <div class="account-menu__actions">
        @if (canSwitchLocale()) {
          <section class="account-menu__locale" [class.account-menu__locale--expanded]="isLocaleMenuExpanded()">
            <button
              type="button"
              class="account-menu__locale-toggle"
              [attr.aria-expanded]="isLocaleMenuExpanded()"
              (click)="toggleLocaleMenu()">
              <i class="account-menu__action-icon fa-duotone fa-solid fa-globe" aria-hidden="true"></i>
              <span class="account-menu__locale-label" i18n>Language</span>
              <span class="account-menu__locale-value">{{ selectedLocaleLabel }}</span>
              <i
                class="account-menu__locale-caret fa-solid"
                [class.fa-chevron-up]="isLocaleMenuExpanded()"
                [class.fa-chevron-down]="!isLocaleMenuExpanded()"
                aria-hidden="true"></i>
            </button>

            @if (isLocaleMenuExpanded()) {
              <div class="account-menu__locale-options" role="group" i18n-aria-label aria-label="Language">
                @for (option of localeOptions; track option.value) {
                  @let isCurrent = option.value === selectedLocale;

                  <button
                    type="button"
                    class="account-menu__locale-option"
                    [class.account-menu__locale-option--current]="isCurrent"
                    [attr.lang]="option.lang"
                    [attr.aria-pressed]="isCurrent"
                    (click)="onLocaleSelected(option.value)">
                    <span class="account-menu__locale-option-name">{{ option.label }}</span>
                    @if (isCurrent) {
                      <span class="account-menu__company-check" aria-hidden="true">
                        <i class="fa-solid fa-check"></i>
                      </span>
                    } @else {
                      <span class="account-menu__company-radio" aria-hidden="true"></span>
                    }
                  </button>
                }
              </div>
            }
          </section>
        }

        <button type="button" class="account-menu__action" [disabled]="isChangingPassword()" (click)="onChangePassword()">
          <i
            class="account-menu__action-icon"
            [class.fa-regular]="!isChangingPassword()"
            [class.fa-solid]="isChangingPassword()"
            [class.fa-key]="!isChangingPassword()"
            [class.fa-spinner-third]="isChangingPassword()"
            [class.fa-spin]="isChangingPassword()"
            aria-hidden="true"></i>
          <span class="account-menu__action-label" i18n>Change password</span>
        </button>

        <button type="button" class="account-menu__action account-menu__action--logout" (click)="onLogout()">
          <i class="account-menu__action-icon fa-solid fa-right-from-bracket" aria-hidden="true"></i>
          <span class="account-menu__action-label" i18n>Log out</span>
        </button>
      </div>
    </pulse-topbar-menu>
  </div>
</div>

@if (mobileCompanySwitcherOpen() && layoutService.isMobileLayout()) {
  <button
    type="button"
    class="account-switcher-mobile__scrim"
    i18n-aria-label
    aria-label="Close company switcher"
    (click)="closeMobileCompanySwitcherFromUi()"></button>

  <div class="account-switcher-mobile__sheet" role="dialog" aria-modal="true" i18n-aria-label aria-label="Switch company">
    <div class="account-switcher-mobile__header">
      <h2 class="account-switcher-mobile__title" i18n>Switch company</h2>
      <button
        type="button"
        class="account-switcher-mobile__close"
        i18n-aria-label
        aria-label="Close company switcher"
        (click)="closeMobileCompanySwitcherFromUi()">
        <i class="fa-regular fa-xmark" aria-hidden="true"></i>
      </button>
    </div>

    <div class="account-switcher-mobile__body">
      <div class="account-menu account-menu--switcher account-menu--mobile-sheet">
        <div class="account-menu__switch-list account-menu__switch-list--mobile">
          <ng-scrollbar class="account-menu__scroll account-menu__scroll--mobile" visibility="hover">
            @if (hasTenantGroups()) {
              @for (tenant of accountTenantGroups(); track tenant.tenantId) {
                @let isExpanded = isTenantExpanded(tenant.tenantId);

                <section class="account-menu__tenant" [class.account-menu__tenant--expanded]="isExpanded">
                  <button
                    type="button"
                    class="account-menu__tenant-toggle"
                    [disabled]="isSwitchingAccount()"
                    [attr.aria-expanded]="isExpanded"
                    (click)="toggleTenant(tenant.tenantId)">
                    <span class="account-menu__org-tile" aria-hidden="true">
                      <i class="fa-regular fa-building"></i>
                    </span>
                    <span class="account-menu__tenant-name">{{ tenant.tenantName }}</span>
                    <span class="account-menu__tenant-count">{{ tenant.companies.length }}</span>
                    @if (!isExpanded && tenantContainsCurrentCompany(tenant)) {
                      <span class="account-menu__tenant-current-dot" aria-hidden="true"></span>
                    }
                    <i
                      class="account-menu__tenant-caret fa-solid"
                      [class.fa-chevron-up]="isExpanded"
                      [class.fa-chevron-down]="!isExpanded"
                      aria-hidden="true"></i>
                  </button>

                  @if (isExpanded) {
                    <div class="account-menu__companies">
                      @for (company of tenant.companies; track company.entityRelationshipId) {
                        @let isCurrent = isCompanyCurrent(company.entityRelationshipId);

                        <button
                          type="button"
                          class="account-menu__company"
                          [class.account-menu__company--current]="isCurrent"
                          [disabled]="isSwitchingAccount()"
                          (click)="selectCompany(company.entityRelationshipId)">
                          <span class="account-menu__company-name">{{ company.companyName }}</span>
                          @if (isCurrent) {
                            <span class="account-menu__company-check" aria-hidden="true">
                              <i class="fa-solid fa-check"></i>
                            </span>
                          } @else {
                            <span class="account-menu__company-radio" aria-hidden="true"></span>
                          }
                        </button>
                      }
                    </div>
                  }
                </section>
              }
            } @else {
              <p class="account-menu__empty" i18n>No account relationships found for your user.</p>
            }
          </ng-scrollbar>
        </div>
      </div>
    </div>
  </div>
}
